import React, { useMemo } from 'react';
import { DatePickerV2 } from './DatePickerV2';
import {
  constraintAnd,
  constraintDayPickerMatcher,
  constraintDisabledTimes,
  constraintIncrementMinute,
  constraintNot,
  constraintTrue,
} from './constraints';
import { CalendarConstraint, DatePickerUnit } from './types';
import { addMilliseconds, set, startOfDay } from 'date-fns';
import { DatePickerProps } from '../types';

/**
 * DatePickerV2를 DatePicker와 동일한 props로 호환시키기 위한 컴포넌트입니다.
 */
export const DatePickerCompat: React.FC<DatePickerProps> = ({
  width,
  size,
  error,
  disabled,
  className,
  showRemoveButton,
  onOpen,
  onClose,
  zIndex,
  displayMode,
  numberOfMonths,
  placeholder,
  value,
  disabledDays,
  showTodayButton,
  displayTimeFormat,
  showTimePicker,
  showFooter,
  unit: unitProp,
  onChange,
  timePickerProps,
  tolerateInvalidValues = true,
}) => {
  const unit = useMemo((): DatePickerUnit => {
    if (unitProp === 'month') {
      return 'month';
    }
    if (showTimePicker) {
      return 'minute';
    }
    return 'day';
  }, [showTimePicker, unitProp]);
  const format = useMemo((): string | undefined => {
    if (displayTimeFormat === 'hourMinute') {
      return 'yyyy.MM.dd HH:mm';
    }
    if (displayTimeFormat === 'hourMinuteSecond') {
      return 'yyyy.MM.dd HH:mm:ss';
    }
    return undefined;
  }, [displayTimeFormat]);
  const constraint = useMemo((): CalendarConstraint => {
    return constraintAnd(
      disabledDays ? constraintNot(constraintDayPickerMatcher(disabledDays)) : constraintTrue(),
      timePickerProps?.disabledTime ? constraintDisabledTimes(timePickerProps.disabledTime) : constraintTrue(),
      timePickerProps?.incrementMinute ? constraintIncrementMinute(timePickerProps.incrementMinute) : constraintTrue(),
    );
  }, [disabledDays, timePickerProps?.disabledTime, timePickerProps?.incrementMinute]);
  const defaultOffset = useMemo((): [Date, Date] | undefined => {
    if (timePickerProps?.defaultTime) {
      const defaultTime = timePickerProps.defaultTime;
      const startDate = set(new Date(), {
        hours: defaultTime.hour,
        minutes: defaultTime.minute,
        seconds: 0,
        milliseconds: 0,
      });
      const endDate = startOfDay(new Date());
      return [startDate, endDate];
    }
  }, [timePickerProps?.defaultTime]);
  return (
    <DatePickerV2
      width={width}
      size={size}
      error={error}
      disabled={disabled}
      className={className}
      showRemoveButton={showRemoveButton}
      zIndex={zIndex}
      displayMode={displayMode}
      numberOfMonths={numberOfMonths}
      placeholder={placeholder}
      constraint={constraint}
      showTodayButton={showTodayButton}
      showControls={showFooter}
      onOpen={onOpen}
      onClose={onClose}
      unit={unit}
      format={format}
      target='all'
      value={value ? [{ id: '', from: value, to: addMilliseconds(value, 1) }] : []}
      onChange={(items) => {
        onChange?.(items[0]?.from ?? null);
      }}
      defaultOffset={defaultOffset}
      timeDisabledWhenEmpty={timePickerProps?.disabledTooltip}
      timeDisabledTooltip={timePickerProps?.disabledTooltipText}
      tolerateInvalidValues={tolerateInvalidValues}
    />
  );
};

export const DatePicker = DatePickerCompat;
